import type { NuxtApp } from "#app";
import type { RuntimeConfig } from "@nuxt/schema";
import * as Sentry from "@sentry/browser";
import type { AxiosError, AxiosInstance } from "axios";
import {
  v2RequestSnakeCaseTransformer,
  v2ResponseCamelCaseTransformer,
} from "mws-utils/lib/caseTransformers/axiosCaseTransformers";
import { dateTransformer } from "mws-utils/lib/dateTransformer";

import { platformKey } from "~/constants/headerKeys";
import getServerSentry from "~/server/helpers/getServerSentry";
import { getAnalyticsHeaders } from "~/utils/analytics";

const NOT_FOUND_MESSAGE = "404 page not found";

/**
 * Sets request headers for client-side API requests.
 * Adds authorization token, platform ID, and analytics tracking context to the request headers.
 *
 * @param request - The Axios request object to modify
 * @param auth - Authentication composable for retrieving user tokens
 * @param platformStore - Platform store containing platform configuration
 * @param nuxtApp - Nuxt application instance for accessing Segment analytics
 * @returns The modified request object with updated headers
 */
async function setRequestHeadersForClientRequest(
  request: any,
  auth: ReturnType<typeof useAuth>,
  platformId: string | undefined,
  nuxtApp: NuxtApp,
) {
  if (!auth || !platformId || !nuxtApp) {
    throw new Error(
      "Unable to set client request headers. Composables undefined.",
    );
  }

  request.headers.Authorization = await auth.getToken();
  request.headers[platformKey] = platformId;
  const analyticsContext = getAnalyticsHeaders(nuxtApp);
  let anonymousId = "";
  if (nuxtApp.$segment.browser.value) {
    const user = await nuxtApp.$segment.browser.value.user();
    anonymousId = user?.anonymousId() ?? "";
  }
  analyticsContext.properties.anonymousId = anonymousId;

  request.headers = {
    ...request.headers,
    trackingContext: JSON.stringify(analyticsContext),
  };

  return request;
}

/**
 * Handles timeout errors for API requests by logging them to Sentry.
 * Redacts sensitive headers (like Authorization) before sending to Sentry.
 *
 * @param error - The Axios error object from a timed-out request
 * @param runtimeConfig - Runtime configuration for accessing Sentry settings
 */
function handleResponseTimeOut(
  error: AxiosError,
  runtimeConfig: RuntimeConfig,
) {
  const errorHeaders: Record<string, any> = {};
  if (error.config?.headers) {
    Object.entries(error.config.headers).forEach(([key, value]) => {
      if (key.toLowerCase() === "authorization") {
        value = "[REDACTED]";
      }

      errorHeaders[key] = value;
    });
  }

  const errorExtra = {
    url: error.config?.url || "UNKNOWN_URL",
    method: error.config?.method?.toUpperCase() || "UNKNOWN_METHOD",
    headers: errorHeaders,
  };

  if (import.meta.client) {
    Sentry.captureException(error, { extra: errorExtra });

    return;
  }

  const serverSentry = getServerSentry(runtimeConfig);
  serverSentry?.setExtras(errorExtra);
  serverSentry?.captureException(error);
}

/**
 * Provides client-side composables and context for Axios interceptors.
 * Returns undefined values when called on the server to prevent SSR errors.
 *
 * @returns Object containing auth, platformStore, and nuxtApp composables (or null on server)
 */
function useRequestContext() {
  if (import.meta.server) {
    return {
      auth: undefined,
      platformStore: undefined,
      nuxtApp: undefined,
    };
  }

  return {
    auth: useAuth(),
    platformStore: usePlatformStore(),
    nuxtApp: useNuxtApp(),
  };
}

/**
 * Configures Axios interceptors for client-side HTTP requests.
 * Sets up request transformers for snake_case conversion, authentication headers,
 * and response transformers for camelCase conversion and error handling.
 *
 * @param axios - The Axios instance to configure with interceptors
 */
export function setAxiosInterceptors(axios: AxiosInstance) {
  const context = useRequestContext();
  const runtimeConfig = useRuntimeConfig();

  axios.defaults.transformResponse = [dateTransformer];
  axios.interceptors.request.use(async (req) => {
    const transformedReq = v2RequestSnakeCaseTransformer(req);

    if (import.meta.server) {
      return transformedReq;
    }

    const platformId =
      context.platformStore?.tryGetPlatformConfiguration?.platformId;

    if (!context.auth) {
      throw new Error(
        "Auth composable is undefined in client request context.",
      );
    }

    return setRequestHeadersForClientRequest(
      transformedReq,
      context.auth,
      platformId,
      context.nuxtApp,
    );
  });

  axios.interceptors.response.use(
    v2ResponseCamelCaseTransformer,
    (error: AxiosError) => {
      if (error.code === "ETIMEDOUT") {
        handleResponseTimeOut(error, runtimeConfig);
      }

      const err = error;
      if ("response" in err) {
        err.response = v2ResponseCamelCaseTransformer(err.response);
      }

      if (err.response?.status === 404) {
        throw createError({
          statusCode: 404,
          statusMessage: NOT_FOUND_MESSAGE,
          message: NOT_FOUND_MESSAGE,
        });
      }

      throw err;
    },
  );
}
